import { useEffect, useState, useCallback, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles, Book, Lightbulb, ListChecks } from "lucide-react";
import { api } from "@/lib/api";

/** Get (or lazily create) an anonymous playground session id in localStorage. */
function getAnonSessionId() {
  try {
    let sid = localStorage.getItem("pm_pg_sid");
    if (!sid) {
      sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("pm_pg_sid", sid);
    }
    return sid;
  } catch {
    // localStorage disabled — fall back to in-memory once
    return `mem-${Math.random().toString(36).slice(2, 12)}`;
  }
}

async function track(event, extra = {}) {
  try {
    await api.post("/analytics/playground", {
      event,
      session_id: getAnonSessionId(),
      referrer: (typeof document !== "undefined" ? document.referrer : "") || "",
      ...extra,
    });
  } catch {
    // silent — never let analytics break the UX
  }
}

/**
 * AnalogyPlayground — full-screen Instagram-Stories-style tap-through
 * that walks anonymous visitors through 4 slides per topic:
 *   1. Analogy hook
 *   2. Image
 *   3. Story
 *   4. Key facts
 * Advances on tap right (or Space / Enter / ArrowRight). Goes back on
 * tap left / ArrowLeft. Closes on X, Esc, or after the last slide.
 *
 * Props:
 *   topics: [{topic_id, topic_name, caption, image_url, analogy, story, key_points}]
 *   startIndex: which topic to open on
 *   onClose: called after close
 *   onSignup: (optional) called when the user taps the final CTA
 */
const STAGES = [
  { key: "analogy", label: "Analogy", icon: Lightbulb },
  { key: "image", label: "Picture it", icon: Sparkles },
  { key: "story", label: "Story", icon: Book },
  { key: "facts", label: "Key facts", icon: ListChecks },
];

function useKey(handler) {
  useEffect(() => {
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handler]);
}

export default function AnalogyPlayground({ topics, startIndex = 0, onClose, onSignup }) {
  const [t, setT] = useState(startIndex);
  const [s, setS] = useState(0);
  const total = topics.length;
  const topic = topics[t];
  const openedRef = useRef(false);
  const completedRef = useRef(false);

  // Fire OPEN once per mount
  useEffect(() => {
    if (!openedRef.current && topic) {
      openedRef.current = true;
      track("open", { topic_id: topic.topic_id, stage: STAGES[0].key });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire STAGE_VIEW on any (t, s) change (except the initial open which is
  // already counted).
  useEffect(() => {
    if (!openedRef.current || !topic) return;
    // Skip first stage/topic pairing already counted by "open"
    if (t === startIndex && s === 0 && openedRef.current) {
      // avoid double-count: only run on subsequent state changes
      return;
    }
    track("stage_view", { topic_id: topic.topic_id, stage: STAGES[s]?.key });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, s]);

  const doClose = useCallback(() => {
    track("close", { topic_id: topic?.topic_id, stage: STAGES[s]?.key });
    onClose && onClose();
  }, [onClose, topic, s]);

  const doSignup = useCallback(() => {
    track("signup_click", { topic_id: topic?.topic_id, stage: STAGES[s]?.key });
    onSignup && onSignup();
  }, [onSignup, topic, s]);

  const next = useCallback(() => {
    if (s < STAGES.length - 1) return setS(s + 1);
    if (t < total - 1) { setT(t + 1); setS(0); return; }
    // Reached end
    if (!completedRef.current) {
      completedRef.current = true;
      track("complete", { topic_id: topic?.topic_id });
    }
    doClose();
  }, [s, t, total, doClose, topic]);

  const prev = useCallback(() => {
    if (s > 0) return setS(s - 1);
    if (t > 0) { setT(t - 1); setS(STAGES.length - 1); return; }
  }, [s, t]);

  useKey(useCallback((e) => {
    if (e.key === "Escape") doClose();
    else if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") { e.preventDefault(); next(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
  }, [next, prev, doClose]));

  if (!topic) return null;
  const stage = STAGES[s];
  const IMAGE_HOST = process.env.REACT_APP_BACKEND_URL;
  const resolvedUrl = topic.image_url?.startsWith("http") ? topic.image_url : `${IMAGE_HOST}${topic.image_url}`;

  // Overall progress across all topics
  const stageBars = topics.flatMap((tt, ti) =>
    STAGES.map((_, si) => ({
      key: `${ti}-${si}`,
      active: ti === t && si === s,
      filled: ti < t || (ti === t && si < s),
    })),
  );

  const bg = {
    analogy: "linear-gradient(135deg,#0F2547 0%,#1a3868 100%)",
    image: "linear-gradient(135deg,#2A5CE0 0%,#3BB6D9 55%,#7B4DFF 100%)",
    story: "linear-gradient(135deg,#3BB6D9 0%,#2A5CE0 100%)",
    facts: "linear-gradient(135deg,#7B4DFF 0%,#2A5CE0 100%)",
  }[stage.key];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(15, 37, 71, 0.85)", backdropFilter: "blur(8px)" }}
      data-testid="playground-overlay"
    >
      <div
        className="relative w-full h-full sm:w-[420px] sm:h-[calc(100vh-40px)] sm:my-5 sm:rounded-3xl overflow-hidden flex flex-col shadow-2xl"
        style={{ background: bg, color: "white" }}
        data-testid="playground-card"
      >
        {/* Progress bars */}
        <div className="flex gap-1 p-3 pt-4" data-testid="playground-progress">
          {stageBars.map((b) => (
            <div
              key={b.key}
              className="flex-1 h-1 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.25)" }}
            >
              <div
                className="h-full transition-all"
                style={{
                  width: b.filled ? "100%" : b.active ? "50%" : "0%",
                  background: "white",
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2">
            <stage.icon size={16} />
            <span className="text-xs font-semibold uppercase tracking-widest opacity-90">{stage.label}</span>
          </div>
          <button
            onClick={doClose}
            className="p-1.5 rounded-full hover:bg-white/10"
            data-testid="playground-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6" data-testid={`playground-slide-${stage.key}`}>
          <h2 className="font-display font-bold text-3xl mb-4 leading-tight" data-testid="playground-topic-name">
            {topic.topic_name}
          </h2>

          {stage.key === "analogy" && (
            <>
              <div className="inline-flex items-center gap-1 text-xs mb-3 px-2 py-1 rounded-full bg-white/15">
                <Lightbulb size={11} /> The trick
              </div>
              <p className="text-lg leading-relaxed" data-testid="playground-analogy-text">
                {topic.analogy || topic.caption || "This concept doesn't have an analogy yet."}
              </p>
            </>
          )}

          {stage.key === "image" && (
            <div className="rounded-2xl overflow-hidden bg-white/10" data-testid="playground-image-frame">
              <img
                src={resolvedUrl}
                alt={topic.topic_name}
                className="w-full h-auto object-cover"
                data-testid="playground-image"
              />
              <div className="p-3 text-sm italic opacity-90">{topic.caption}</div>
            </div>
          )}

          {stage.key === "story" && (
            <>
              <div className="inline-flex items-center gap-1 text-xs mb-3 px-2 py-1 rounded-full bg-white/15">
                <Book size={11} /> Story
              </div>
              <p className="text-base leading-relaxed whitespace-pre-wrap" data-testid="playground-story-text">
                {topic.story || "This concept doesn't have a story yet."}
              </p>
            </>
          )}

          {stage.key === "facts" && (
            <>
              <div className="inline-flex items-center gap-1 text-xs mb-3 px-2 py-1 rounded-full bg-white/15">
                <ListChecks size={11} /> Remember these
              </div>
              {topic.key_points && topic.key_points.length > 0 ? (
                <ul className="space-y-2" data-testid="playground-facts-list">
                  {topic.key_points.map((k, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed">
                      <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-white/20 text-xs font-bold">
                        {i + 1}
                      </div>
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm opacity-80">Key facts unlock inside the app.</p>
              )}

              {/* CTA on the last slide of the last topic */}
              {t === total - 1 && (
                <div className="mt-6 p-4 rounded-2xl bg-white text-[color:var(--text)] relative z-20">
                  <div className="font-display font-bold text-lg mb-1" style={{ color: "#0F2547" }}>
                    Want the other 1,300+ topics like this?
                  </div>
                  <p className="text-sm mb-3" style={{ color: "#465974" }}>
                    Free plan · 6 one-time credits · No card needed
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); doSignup(); }}
                    className="pm-btn-primary w-full"
                    data-testid="playground-signup-cta"
                  >
                    Start free
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Tap zones (kept behind interactive content via z-0) */}
        <button
          onClick={prev}
          className="absolute left-0 top-16 bottom-24 w-1/3 z-0"
          aria-label="Previous"
          data-testid="playground-tap-prev"
          style={{ background: "transparent" }}
        />
        <button
          onClick={next}
          className="absolute right-0 top-16 bottom-24 w-2/3 z-0"
          aria-label="Next"
          data-testid="playground-tap-next"
          style={{ background: "transparent" }}
        />

        {/* Footer nav */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/15">
          <button
            onClick={prev}
            disabled={t === 0 && s === 0}
            className="p-2 rounded-full disabled:opacity-30 hover:bg-white/10"
            data-testid="playground-prev-btn"
            aria-label="Previous"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="text-xs opacity-80">
            {t + 1} of {total}
          </div>
          <button
            onClick={next}
            className="p-2 rounded-full hover:bg-white/10"
            data-testid="playground-next-btn"
            aria-label="Next"
          >
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
